"use client"

import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { cn } from "../../utils/tailwindUtils"
import { Check, Circle } from "lucide-react"

// Context for Dropdown state
const DropdownContext = createContext(null)

const useDropdown = () => {
  const ctx = useContext(DropdownContext)
  if (!ctx) throw new Error("Dropdown components must be inside <DropdownMenu>")
  return ctx
}

// Root
const DropdownMenu = ({ children }) => {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef(null)

  return (
    <DropdownContext.Provider value={{ open, setOpen, triggerRef }}>
      <div className="relative inline-block">{children}</div>
    </DropdownContext.Provider>
  )
}

// Trigger
const DropdownMenuTrigger = React.forwardRef(
  ({ children, className, asChild = false, ...props }, ref) => {
    const { setOpen, triggerRef, open } = useDropdown()

    const mergedRef = (node) => {
      triggerRef.current = node
      if (ref) {
        if (typeof ref === "function") ref(node)
        else ref.current = node
      }
    }

    if (asChild && React.isValidElement(children)) {
      // Clone child and pass trigger props
      return React.cloneElement(children, {
        ref: mergedRef,
        onClick: (e) => {
          children.props.onClick?.(e)
          setOpen(!open)
        },
        className: cn("rounded-md hover:bg-accent", children.props.className, className),
        ...props,
      })
    }

    // Default fallback: <button>
    return (
      <button
        ref={mergedRef}
        onClick={() => setOpen(!open)}
        className={cn("rounded-md hover:bg-accent", className)}
        {...props}
      >
        {children}
      </button>
    )
  }
)

DropdownMenuTrigger.displayName = "DropdownMenuTrigger"

const clampSidebarFlyout = ({ trigger, side, align, sideOffset, alignOffset, padding, vh, vw, naturalHeight, naturalWidth }) => {
  let top = 0
  let left = 0
  let transform

  switch (side) {
    case "top":
      top = trigger.top - sideOffset
      left = trigger.left + alignOffset
      transform = "translateY(-100%)"
      break
    case "right":
      left = trigger.right + sideOffset
      top = trigger.top + alignOffset
      if (align === "end") top = trigger.bottom - naturalHeight
      else if (align === "center") top = trigger.top + trigger.height / 2 - naturalHeight / 2
      break
    case "left":
      left = trigger.left - sideOffset
      transform = "translateX(-100%)"
      top = trigger.top + alignOffset
      if (align === "end") top = trigger.bottom - naturalHeight
      else if (align === "center") top = trigger.top + trigger.height / 2 - naturalHeight / 2
      break
    default:
      top = trigger.bottom + sideOffset
      left = trigger.left + alignOffset
      if (align === "end") left = trigger.right - naturalWidth
      else if (align === "center") left = trigger.left + trigger.width / 2 - naturalWidth / 2
  }

  if (side === "right" && left + naturalWidth > vw - padding) {
    left = Math.max(padding, trigger.left - sideOffset - naturalWidth)
    transform = "translateX(-100%)"
  }

  const maxViewportHeight = vh - padding * 2
  let maxHeight = Math.min(naturalHeight, maxViewportHeight)

  if (top + maxHeight > vh - padding) {
    top = Math.max(padding, vh - padding - maxHeight)
  }

  if (top < padding) {
    top = padding
    maxHeight = Math.min(naturalHeight, vh - padding * 2)
  }

  return { top, left, transform, maxHeight }
}

// Content
const DropdownMenuContent = React.forwardRef(
  ({ children, className, side = "bottom", align = "start", sideOffset = 4, alignOffset = 0, collisionPadding = 8, ...props }, ref) => {    
    const { open, setOpen, triggerRef } = useDropdown()

    const contentRef = useRef(null)
    const setRefs = useCallback(
      (node) => {
        contentRef.current = node
        if (typeof ref === "function") ref(node)
        else if (ref) ref.current = node
      },
      [ref]
    )

    const [position, setPosition] = useState({
      top: 0,
      left: 0,
      transform: undefined,
      maxHeight: undefined,
    })
    const [layoutTick, setLayoutTick] = useState(0)
    const [isPositioned, setIsPositioned] = useState(false)

    useEffect(() => {
      if (!open) setIsPositioned(false)
    }, [open])

    useLayoutEffect(() => {
      if (!open || !triggerRef.current || !contentRef.current) return

      const measureAndPosition = () => {
        if (!triggerRef.current || !contentRef.current) return

        const trigger = triggerRef.current.getBoundingClientRect()
        const content = contentRef.current
        const padding = collisionPadding
        const vw = window.innerWidth
        const vh = window.innerHeight

        content.style.maxHeight = "none"
        content.style.overflowY = "visible"
        const naturalHeight = content.scrollHeight
        const naturalWidth = content.offsetWidth

        setPosition(
          clampSidebarFlyout({
            trigger,
            side,
            align,
            sideOffset,
            alignOffset,
            padding,
            vh,
            vw,
            naturalHeight,
            naturalWidth,
          }),
        )
        setIsPositioned(true)
      }

      measureAndPosition()
      const raf = requestAnimationFrame(measureAndPosition)
      return () => cancelAnimationFrame(raf)
    }, [open, triggerRef, side, align, sideOffset, alignOffset, collisionPadding, children, layoutTick])

    useEffect(() => {
      if (!open) return undefined

      const reposition = () => setLayoutTick((tick) => tick + 1)

      window.addEventListener("resize", reposition)
      window.addEventListener("scroll", reposition, true)
      return () => {
        window.removeEventListener("resize", reposition)
        window.removeEventListener("scroll", reposition, true)
      }
    }, [open])

    // Close on outside click (capture phase) + ESC
    useEffect(() => {
      if (!open) return

      const onPointerDown = (e) => {
        const c = contentRef.current
        const t = triggerRef.current
        if (!c || !t) return
        if (c.contains(e.target) || t.contains(e.target)) return
        setOpen(false)
      }

      const onKeyDown = (e) => {
        if (e.key === "Escape") setOpen(false)
      }

      document.addEventListener("pointerdown", onPointerDown, true)
      document.addEventListener("keydown", onKeyDown)

      return () => {
        document.removeEventListener("pointerdown", onPointerDown, true)
        document.removeEventListener("keydown", onKeyDown)
      }
    }, [open, setOpen, triggerRef])

    if (!open) return null

    const menu = (
      <div
        ref={setRefs}
        data-side={side}
        data-state={open ? "open" : "closed"}
        className={cn(
          "fixed z-[200] min-w-[15rem] overflow-x-hidden overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-lg",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "data-[side=bottom]:slide-in-from-top-2",
          "data-[side=left]:slide-in-from-right-2",
          "data-[side=right]:slide-in-from-left-2",
          "data-[side=top]:slide-in-from-bottom-2",
          className
        )}
        style={{
          top: position.top,
          left: position.left,
          transform: position.transform,
          maxHeight: position.maxHeight,
          visibility: isPositioned ? "visible" : "hidden",
        }}
        {...props}
      >
        {children}
      </div>
    )

    return typeof document !== "undefined" ? createPortal(menu, document.body) : menu
  }
)

DropdownMenuContent.displayName = "DropdownMenuContent"

// Item
const DropdownMenuItem = React.forwardRef(
  ({ children, className, isActive, inset, onClick, ...props }, ref) => {
    const { setOpen } = useDropdown()

    return (
      <div
        ref={ref}
        onClick={(e) => {
          onClick?.(e)
          setOpen(false)
        }}
        className={cn(
          " relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors",
          inset && "pl-8",
          isActive 
            ? "bg-accent text-accent-foreground"  // active state
            : "hover:bg-accent hover:text-accent-foreground", // normal hover
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }
)

DropdownMenuItem.displayName = "DropdownMenuItem"

// Checkbox Item
const DropdownMenuCheckboxItem = React.forwardRef(({ children, checked, className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "relative flex cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm hover:bg-accent hover:text-accent-foreground",
        className
      )}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        {checked && <Check className="h-4 w-4" />}
      </span>
      {children}
    </div>
  )
})
DropdownMenuCheckboxItem.displayName = "DropdownMenuCheckboxItem"

// Radio Group
const DropdownMenuRadioGroup = ({ value, onValueChange, children }) => {
  return (
    <div role="radiogroup">
      {React.Children.map(children, (child) =>
        React.cloneElement(child, { checked: child.props.value === value, onValueChange })
      )}
    </div>
  )
}

const DropdownMenuRadioItem = React.forwardRef(({ children, value, checked, onValueChange, className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      onClick={() => onValueChange && onValueChange(value)}
      className={cn(
        "relative flex cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm hover:bg-accent hover:text-accent-foreground",
        className
      )}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        {checked && <Circle className="h-2 w-2 fill-current" />}
      </span>
      {children}
    </div>
  )
})
DropdownMenuRadioItem.displayName = "DropdownMenuRadioItem"

// Label
const DropdownMenuLabel = React.forwardRef(({ children, className, inset, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("px-2 py-1.5 text-sm font-semibold", inset && "pl-8", className)}
    {...props}
  >
    {children}
  </div>
))
DropdownMenuLabel.displayName = "DropdownMenuLabel"

// Separator
const DropdownMenuSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("-mx-1 my-1 h-px bg-muted", className)} {...props} />
))
DropdownMenuSeparator.displayName = "DropdownMenuSeparator"

// Shortcut
const DropdownMenuShortcut = ({ className, ...props }) => {
  return <span className={cn("ml-auto text-xs tracking-widest opacity-60", className)} {...props} />
}
DropdownMenuShortcut.displayName = "DropdownMenuShortcut"

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
}
